// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import transformContentHtml from "@insite/client-framework/Common/Utilities/transformContentHtml";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import * as React from "react";
import { css } from "styled-components";

type Props = WidgetProps & HasProduct;

export interface ProductDetailsDescriptionStyles {
    wrapper?: InjectableCss;
    title?: TypographyProps;
}

export const descriptionStyles: ProductDetailsDescriptionStyles = {
    wrapper: {
        css: css`
            margin-bottom: 0;
            div[data-test-selector="productDetails_htmlContent"] {
                line-height: 21px;
            }
        `,
    },
    title: {
        css: css`
            margin-bottom: 12px;
        `,
    },
};

const styles = descriptionStyles;

const ProductDetailsDescription: React.FC<Props> = ({ product }) => {
    // XNG-Change display up to 1000 chars and add view less, view more links
    const [allContentIsVisible, setAllContentIsVisible] = React.useState(false);

    // XNG-Change
    if (!product.content || !product.content.htmlContent) {
        return null;
    }
    const description = parse(transformContentHtml(product.content.htmlContent), parserOptions);

    // XNG-Change add description title and view more and less links
    return (
        <StyledWrapper {...styles.wrapper}>
            <Typography {...styles.title} variant="h5" as="p">
                {translate("Description")}
            </Typography>
            <div data-test-selector="productDetails_htmlContent">
                {!allContentIsVisible && description.toString().length > 1000
                    ? description.toString().substring(0, 1000)
                    : description}
                {!allContentIsVisible && description.toString().length > 1000 && (
                    <Link onClick={() => setAllContentIsVisible(true)} aria-label="View More">
                        {translate("View More")}
                    </Link>
                )}
                {allContentIsVisible && description.toString().length > 1000 && (
                    <Link onClick={() => setAllContentIsVisible(false)} aria-label="View Less">
                        {translate("View Less")}
                    </Link>
                )}
            </div>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(ProductDetailsDescription),
    definition: {
        displayName: "Description",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
